<template>
  <div class="max-w-5xl mx-auto space-y-8">
    <el-form ref="formRef" :model="formData" :rules="rules" label-width="120px" label-position="top">
      <!-- 成果归属 -->
      <AchievementTypeSection
        :achievement-type="formData.achievementType"
        :team-members="formData.teamMembers"
        @update:achievement-type="handleAchievementTypeChange"
        @update:team-members="handleTeamMembersChange"
      />

      <!-- 专著基本信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">专著基本信息</h3>
        <div class="space-y-4">
          <el-form-item label="专著名称" prop="name" required>
            <el-input
              v-model="formData.name"
              placeholder="请输入专著名称"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="ISBN号" prop="isbn" required>
            <el-input
              v-model="formData.isbn"
              placeholder="请输入ISBN号"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 专著类型与研究信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">专著类型与研究信息</h3>
        <div class="space-y-4">
          <el-form-item label="专著类别" prop="mono_type" required>
            <el-select v-model="formData.mono_type" placeholder="请选择专著类别" class="custom-select">
              <el-option value="1" label="专著" />
              <el-option value="2" label="译著" />
              <el-option value="3" label="辞书" />
            </el-select>
          </el-form-item>
          <el-form-item label="学科类别" prop="subject" required>
            <el-input
              v-model="formData.subject"
              placeholder="请输入具体学科名称"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 成果来源信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">成果来源信息</h3>
        <div class="space-y-4">
          <el-form-item label="成果来源名称">
            <el-input
              v-model="formData.source_name"
              placeholder="注明课题年度"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="成果来源级别">
            <el-select v-model="formData.source_level" placeholder="请选择成果来源级别" class="custom-select">
              <el-option value="1" label="国家级" />
              <el-option value="2" label="省部级" />
              <el-option value="3" label="市级" />
              <el-option value="4" label="校级" />
            </el-select>
          </el-form-item>
        </div>
      </div>

      <!-- 出版信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">出版信息</h3>
        <div class="space-y-4">
          <el-form-item label="出版社" prop="publisher" required>
            <el-input
              v-model="formData.publisher"
              placeholder="请输入出版社名称"
              class="custom-input"
            />
          </el-form-item>
          <el-form-item label="出版时间" prop="pub_time" required>
            <el-date-picker
              v-model="formData.pub_time"
              type="date"
              placeholder="选择出版时间"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="custom-input w-full"
              style="width: 100%"
            />
          </el-form-item>
        </div>
      </div>



      <!-- 其他信息 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">其他信息</h3>
        <div class="space-y-4">
          <el-form-item label="备注">
            <el-input
              v-model="formData.remark"
              type="textarea"
              :rows="3"
              placeholder="请输入备注信息"
              class="custom-input"
            />
          </el-form-item>
        </div>
      </div>

      <!-- 佐证材料 -->
      <div class="space-y-6">
        <h3 class="text-lg text-blue-600 border-b border-gray-200 pb-2 mt-8">佐证材料</h3>
        <FileUpload 
          ref="uploadRef"
          title="佐证材料上传" 
          description="请上传专著封面、版权页或出版证明" 
          @success="onUploadSuccess"
          @change="onUploadChange"
        />
      </div>

      <!-- 提交按钮 -->
      <div class="flex justify-end space-x-4 pt-6 border-t border-gray-200">
        <el-button 
          @click="submitForm(true)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none border bg-white text-gray-900 hover:bg-gray-50 hover:text-gray-900 h-9 px-4 py-2"
        >
          保存草稿
        </el-button>
        <el-button 
          @click="submitForm(false)"
          class="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none bg-blue-600 text-white hover:bg-blue-700 h-9 px-4 py-2 border-0"
        >
          提交
        </el-button>
      </div>
    </el-form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue'
import FileUpload from '../common/FileUpload.vue'
import AchievementTypeSection from '../common/AchievementTypeSection.vue'
import { saveMonograph, submitMonograph, updateMonograph, getMonograph } from '../../api/monograph'
import { ElMessage } from 'element-plus'
import { ensureValidTeamMembers } from '../../utils/teamMembers'
import { useRoute } from 'vue-router'
import { getMyInfo } from '../../api/user'

const formData = reactive({
  achievementType: '',
  isFirstAuthor: '',
  isFirstInUnit: '',
  teamMembers: [],
  name: "",
  isbn: "",
  mono_type: "",
  subject: "",
  source_name: "",
  source_level: "",
  publisher: "",
  pub_time: "",
  remark: ""
})

const currentId = ref(null)
const loading = ref(false)
const evidenceMaterial = ref('')
const evidenceMaterialList = ref([])
const myInfo = ref(null)
const formRef = ref(null)
const uploadRef = ref(null)
const LAST_DRAFT_KEY = 'monograph:lastDraftId'
const DRAFT_FORM_KEY = 'monograph:draftForm'
const DRAFT_STATUS_KEY = 'monograph:draftStatus'

const rules = reactive({
  name: [{ required: true, message: '请输入专著名称', trigger: 'blur' }],
  isbn: [{ required: true, message: '请输入ISBN号', trigger: 'blur' }],
  mono_type: [{ required: true, message: '请选择专著类别', trigger: 'change' }],
  subject: [
    { required: true, message: '请输入学科类别', trigger: 'blur' },
    { validator: subjectCategoryValidator, trigger: 'blur' }
  ],
  publisher: [{ required: true, message: '请输入出版社名称', trigger: 'blur' }],
  pub_time: [{ required: true, message: '请选择出版时间', trigger: 'change' }]
})

function subjectCategoryValidator(rule, value, callback) {
  const v = value == null ? '' : String(value).trim()
  if (!v) return callback()
  if (/^\d+$/.test(v)) return callback(new Error('请填写规范的学科分类中文名'))
  return callback()
}

// 处理成果归属变化
const handleAchievementTypeChange = (value) => {
  formData.achievementType = value
}


const handleTeamMembersChange = (members) => {
  formData.teamMembers = members
}

function onUploadSuccess({ url }) {
  if (url && !evidenceMaterialList.value.includes(url)) {
    evidenceMaterialList.value.push(url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

function onUploadChange({ url, type }) {
  if (type === 'remove' && url) {
    evidenceMaterialList.value = evidenceMaterialList.value.filter(u => u !== url)
    evidenceMaterial.value = evidenceMaterialList.value.join(',')
  }
}

function sanitizeEvidenceMaterial(s) {
  const v = String(s || '')
  if (!v) return ''
  return v
    .split(',')
    .map(p => String(p || '').trim().replace(/`/g, ''))
    .filter(Boolean)
    .join(',')
}

function isValidUrl(u) {
  return /^https?:\/\/\S+$/.test(String(u || ''))
}

function filterValidEvidence(s) {
  const cleaned = sanitizeEvidenceMaterial(s)
  if (!cleaned) return ''
  const parts = cleaned.split(',').map(x => x.trim()).filter(Boolean)
  const valid = parts.filter(isValidUrl)
  return valid.join(',')
}

const IDENTITY_LABELS = { teacher: '教师', student: '学生', social: '社会人士' }
function toIdentityLabel(v) {
  const key = String(v || '')
  return IDENTITY_LABELS[key] ?? key
}

function normalizeAchievementType(v) {
  if (v === 'individual') return 1
  if (v === 'team') return 2
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

function denormalizeAchievementType(v) {
  if (v === 1 || v === '1') return 'individual'
  if (v === 2 || v === '2') return 'team'
  return ''
}

function normalizeMembers(members = []) {
  return (Array.isArray(members) ? members : []).map((m, idx) => ({
    position: m?.rank != null ? parseInt(m.rank, 10) || idx + 1 : idx + 1,
    name: m?.name || '',
    identity: toIdentityLabel(m?.identity),
    unit: m?.unit || ''
  }))
}

function valueToLabelMonographType(v) {
  if (v === '1') return '专著'
  if (v === '2') return '译著'
  if (v === '3') return '辞书'
  return String(v || '')
}

function valueToLabelSourceLevel(v) {
  if (v === '1') return '国家级'
  if (v === '2') return '省部级'
  if (v === '3') return '市级'
  if (v === '4') return '校级'
  return String(v || '')
}

function formatDateTimePlain(input) {
  if (!input) return undefined
  const d = typeof input === 'string' ? new Date(input) : input
  if (!(d instanceof Date) || isNaN(d.getTime())) return undefined
  const pad = (n) => String(n).padStart(2, '0')
  const y = d.getFullYear()
  const m = pad(d.getMonth() + 1)
  const day = pad(d.getDate())
  const h = pad(d.getHours())
  const min = pad(d.getMinutes())
  const s = pad(d.getSeconds())
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

function mapFormToApi(data, isDraft, id) {
  const publishTime = formatDateTimePlain(data.pub_time)
  const dto = {
    id: id != null ? Number(id) : undefined,
    achievementType: normalizeAchievementType(data.achievementType),
    monographName: data.name || '',
    isbn: data.isbn || '',
    monographType: valueToLabelMonographType(data.mono_type || ''),
    subjectCategory: data.subject || '',
    achievementSourceName: data.source_name || '',
    achievementSourceLevel: valueToLabelSourceLevel(data.source_level || ''),
    publisher: data.publisher || '',
    remark: data.remark || '',
    evidenceMaterial: filterValidEvidence(evidenceMaterial.value || ''),
    status: isDraft ? 1 : 2,
    memberList: normalizeMembers(data.teamMembers)
  }
  if (publishTime) dto.publishTime = publishTime
  if (dto.achievementType === 1 && (!Array.isArray(dto.memberList) || dto.memberList.length === 0)) {
    const info = myInfo.value || {}
    const name = String(info?.name || '')
    const identity = String(info?.role || '教师')
    const unit = String(info?.college || '')
    dto.memberList = name || unit ? [{ position: 1, name, identity, unit }] : []
  }
  return dto
}

function mapApiToForm(dto = {}) {
  formData.achievementType = denormalizeAchievementType(dto.achievementType)
  formData.teamMembers = Array.isArray(dto.memberList)
    ? dto.memberList.map((m, i) => ({
        id: `${Date.now()}_${i}`,
        name: String(m?.name || ''),
        identity: String(m?.identity || ''),
        unit: String(m?.unit || ''),
        rank: Number(m?.position ?? i + 1) || i + 1
      }))
    : []

  formData.name = dto.monographName ?? ''
  formData.isbn = dto.isbn ?? ''
  const mt = String(dto.monographType ?? '')
  formData.mono_type = mt === '专著' ? '1' : mt === '译著' ? '2' : mt === '辞书' ? '3' : ''
  formData.subject = dto.subjectCategory ?? ''
  formData.source_name = dto.achievementSourceName ?? ''
  const sl = String(dto.achievementSourceLevel ?? '')
  formData.source_level = sl === '国家级' ? '1' : sl === '省部级' ? '2' : sl === '市级' ? '3' : sl === '校级' ? '4' : ''
  formData.publisher = dto.publisher ?? ''
  formData.pub_time = dto.publishTime ? new Date(dto.publishTime) : ''
  formData.remark = dto.remark ?? ''
  const em = dto.evidenceMaterial || ''
  evidenceMaterial.value = em
  evidenceMaterialList.value = em ? String(em).split(',').filter(Boolean) : []
}

const submitForm = async (isDraft = false) => {
  try {
    loading.value = true
    const ok = await (formRef.value?.validate?.() ?? Promise.resolve(true)).catch(() => false)
    if (!ok) {
      ElMessage.error('请完善所有必填项')
      loading.value = false
      return
    }
    if (!formData.achievementType) {
      ElMessage.error('请选择成果归属')
      loading.value = false
      return
    }
    if (formData.achievementType === 'team') {
      const { ok, message } = ensureValidTeamMembers(formData.achievementType, formData.teamMembers)
      if (!ok) {
        ElMessage.error(message || '团队成员信息未填写完整')
        loading.value = false
        return
      }
    }
    const base = mapFormToApi(formData, isDraft, currentId.value)
    if (!currentId.value) {
      if (isDraft) {
        const resp = await saveMonograph(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      } else {
        const resp = await submitMonograph(base)
        const id = resp?.id ?? resp?.data?.id ?? resp?.data?.data?.id
        if (id) currentId.value = id
      }
    } else {
      const updateDto = { ...base, id: Number(currentId.value), status: isDraft ? 1 : 2 }
      await updateMonograph(updateDto)
    }
    if (isDraft) {
      if (!currentId.value) {
        try {
          localStorage.setItem(LAST_DRAFT_KEY, String(currentId.value))
          localStorage.setItem(DRAFT_STATUS_KEY, '1')
          const snapshot = {
            form: { ...formData },
            evidenceMaterial: evidenceMaterial.value || '',
            evidenceMaterialList: Array.isArray(evidenceMaterialList.value) ? [...evidenceMaterialList.value] : [],
            currentId: currentId.value || null
          }
          localStorage.setItem(DRAFT_FORM_KEY, JSON.stringify(snapshot))
        } catch (e) { console.warn(e) }
      }
    } else {
      try { await nextTick(); formRef.value?.clearValidate?.(); formRef.value?.resetFields?.() } catch (e) { console.warn(e) }
      currentId.value = null
      try { localStorage.removeItem(LAST_DRAFT_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_FORM_KEY) } catch (e) { console.warn(e) }
      try { localStorage.removeItem(DRAFT_STATUS_KEY) } catch (e) { console.warn(e) }
      try { resetForm() } catch (e) { console.warn(e) }
    }
    ElMessage.success(isDraft ? '草稿保存成功' : '提交成功')
  } catch {
    ElMessage.error(isDraft ? '草稿保存失败' : '提交失败')
  } finally {
    loading.value = false
  }
}

const route = useRoute()
onMounted(async () => {
  try {
    const info = await getMyInfo()
    const data = info?.data ?? info
    myInfo.value = data
  } catch (e) { console.warn(e) }
  const rid = route.query?.detailId || route.query?.id  // ← 优先用 detailId
  if (rid) {
    try {
      currentId.value = rid
      const dto = await getMonograph(rid)
      mapApiToForm(dto?.data ?? dto)
    } catch (e) { console.warn(e) }
  } else {
    try {
      const draftFlag = localStorage.getItem(DRAFT_STATUS_KEY)
      if (draftFlag === '1') {
        const raw = localStorage.getItem(DRAFT_FORM_KEY)
        if (raw) {
          try {
            const snapshot = JSON.parse(raw)
            const f = snapshot?.form || {}
            Object.assign(formData, f)
            evidenceMaterial.value = snapshot?.evidenceMaterial || ''
            evidenceMaterialList.value = Array.isArray(snapshot?.evidenceMaterialList) ? [...snapshot.evidenceMaterialList] : []
            const id = snapshot?.currentId || localStorage.getItem(LAST_DRAFT_KEY)
            if (id) currentId.value = id
          } catch (e) { console.warn(e) }
        }
      }
    } catch (e) { console.warn(e) }
  }
})
const resetForm = () => {
  Object.assign(formData, {
    achievementType: '',
    isFirstAuthor: '',
    isFirstInUnit: '',
    teamMembers: [],
    name: '',
    isbn: '',
    mono_type: '',
    subject: '',
    source_name: '',
    source_level: '',
    publisher: '',
    pub_time: '',
    remark: ''
  })
  evidenceMaterial.value = ''
  evidenceMaterialList.value = []
  currentId.value = null
  try { uploadRef.value?.clear?.() } catch (e) { console.warn(e) }
}
</script>

<style scoped>
/* 自定义Select样式 */
:deep(.custom-select .el-select__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-select .el-select__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-select .el-select__wrapper.is-focused) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-select .el-select__placeholder) {
  color: #9ca3af;
}

:deep(.custom-select .el-select__input) {
  color: #111827;
  background-color: transparent;
}

/* 自定义Input样式，与Select保持一致 */
:deep(.custom-input .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-input .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-input .el-input__inner) {
  color: #111827;
  background-color: transparent;
}

:deep(.custom-input .el-input__inner::placeholder) {
  color: #9ca3af;
}

/* Textarea样式 */
:deep(.custom-input .el-textarea__inner) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  color: #111827;
}

:deep(.custom-input .el-textarea__inner:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-textarea__inner:focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

:deep(.custom-input .el-textarea__inner::placeholder) {
  color: #9ca3af;
}

/* 日期选择器样式 */
:deep(.custom-input .el-date-editor.el-input) {
  width: 100%;
}

:deep(.custom-input .el-date-editor .el-input__wrapper) {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background-color: #f9fafb;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

:deep(.custom-input .el-date-editor .el-input__wrapper:hover) {
  border-color: #9ca3af;
  background-color: #f3f4f6;
}

:deep(.custom-input .el-date-editor .el-input__wrapper.is-focus) {
  border-color: #2563eb;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  background-color: white;
}

/* 单选按钮样式 */
.scale-\[0\.4\] {
  transform: scale(0.4);
}
</style>